import React, { createContext, useContext, useMemo } from "react";
import {
  useProgramIteration,
  useRoomIteration,
  usePosIteration,
  isCollectionBindingKey,
  resolveCollectionBinding,
  type CollectionBindingKey,
} from "./MacroCollectionContext";

// Binding keys available for macro templates
export type MacroBindingKey =
  | "FirstName"
  | "ProgramDaysText"
  | "ReservationId"
  | "RoomCount"
  | "TotalFitnessCount"
  | "WeightLossCount"
  | "QuoteLink"
  // Payment/deposit bindings for BookingPaymentRequest macros
  | "DepositPercent"
  | "DepositAmount"
  | "TotalPrice"
  | "RemainderAmount"
  | "FullPaymentDueDate"
  | "PaymentLinkFull"
  | "PaymentLinkPartial"
  | "RemainderDueText"
  // Payment link macro bindings
  | "PaymentProvider"
  | "FullFeePercent"
  | "PartialFeePercent"
  | "IsPaymentDueDatePassed";

// Array of all binding keys for iteration
export const MACRO_BINDING_KEYS: MacroBindingKey[] = [
  "FirstName",
  "ProgramDaysText",
  "ReservationId",
  "RoomCount",
  "TotalFitnessCount",
  "WeightLossCount",
  "QuoteLink",
  "DepositPercent",
  "DepositAmount",
  "TotalPrice",
  "RemainderAmount",
  "FullPaymentDueDate",
  "PaymentLinkFull",
  "PaymentLinkPartial",
  "RemainderDueText",
  // Payment link macro bindings
  "PaymentProvider",
  "FullFeePercent",
  "PartialFeePercent",
  "IsPaymentDueDatePassed",
];

// Values passed to macro rendering
export interface MacroBindingValues {
  FirstName: string;
  ProgramDaysText: string;
  ReservationId: string;
  RoomCount: number;
  TotalFitnessCount: number;
  WeightLossCount: number;
  QuoteLink: string;
  // Payment/deposit values for BookingPaymentRequest macros
  DepositPercent: number;
  DepositAmount: string; // Formatted as "฿XX,XXX"
  TotalPrice: string; // Formatted as "฿XX,XXX"
  RemainderAmount: string; // Formatted as "฿XX,XXX"
  FullPaymentDueDate: string; // Formatted date
  PaymentLinkFull: string;
  PaymentLinkPartial: string;
  RemainderDueText: string; // e.g., "The remaining ฿25,000 is due by March 1, 2025"
  // Payment link macro values
  PaymentProvider: string; // "Omise" or "Stripe"
  FullFeePercent: string; // e.g., "1.85"
  PartialFeePercent: string; // e.g., "3.65"
  IsPaymentDueDatePassed: boolean; // true if 4 weeks before arrival has passed
}

// Default values for preview in editor
export const defaultPreviewBindings: MacroBindingValues = {
  FirstName: "John",
  ProgramDaysText: "14 days",
  ReservationId: "00000000-0000-0000-0000-000000000000",
  RoomCount: 1,
  TotalFitnessCount: 1,
  WeightLossCount: 0,
  QuoteLink: "https://reservations.phuketfit.com/quotes/sample",
  // Payment/deposit defaults
  DepositPercent: 50,
  DepositAmount: "฿25,000",
  TotalPrice: "฿50,000",
  RemainderAmount: "฿25,000",
  FullPaymentDueDate: "March 1, 2025",
  PaymentLinkFull: "https://pay.stripe.com/sample-full",
  PaymentLinkPartial: "https://pay.stripe.com/sample-partial",
  RemainderDueText: "The remaining ฿25,000 is due by March 1, 2025",
  // Payment link macro defaults
  PaymentProvider: "Stripe",
  // Fee recovery disabled — see ReservationsApp/.../paymentFeeRates.ts (FEE_RECOVERY_ENABLED).
  FullFeePercent: "0",
  PartialFeePercent: "0",
  IsPaymentDueDatePassed: false,
};

// Binding options for the editor UI (dropdown selection)
export interface BindingOption {
  key: MacroBindingKey;
  label: string;
  description: string;
}

export const bindingOptions: BindingOption[] = [
  { key: "FirstName", label: "First Name", description: "Guest's first name" },
  { key: "ProgramDaysText", label: "Program Days", description: "Duration text (e.g., '14 days')" },
  { key: "ReservationId", label: "Reservation ID", description: "Unique reservation identifier" },
  { key: "RoomCount", label: "Room Count", description: "Number of rooms booked" },
  { key: "TotalFitnessCount", label: "Total Fitness Count", description: "Number of Total Fitness programs" },
  { key: "WeightLossCount", label: "Weight Loss Count", description: "Number of Weight Loss programs" },
  { key: "QuoteLink", label: "Quote Link", description: "Link to view the quote" },
  // Payment/deposit bindings
  { key: "DepositPercent", label: "Deposit %", description: "Deposit percentage number only (e.g., 50)" },
  { key: "DepositAmount", label: "Deposit Amount", description: "Formatted WITH currency (e.g., '฿25,000') - DO NOT add ฿ before this" },
  { key: "TotalPrice", label: "Total Price", description: "Formatted WITH currency (e.g., '฿50,000') - DO NOT add ฿ before this" },
  { key: "RemainderAmount", label: "Remainder Amount", description: "Formatted WITH currency (e.g., '฿25,000') - DO NOT add ฿ before this" },
  { key: "FullPaymentDueDate", label: "Payment Due Date", description: "Date remainder is due (e.g., March 1, 2025)" },
  { key: "PaymentLinkFull", label: "Full Payment Link", description: "URL for full payment" },
  { key: "PaymentLinkPartial", label: "Partial Payment Link", description: "URL for deposit payment" },
  { key: "RemainderDueText", label: "Remainder Due Text", description: "Full sentence about remainder due (e.g., 'The remaining ฿25,000 is due by March 1, 2025')" },
  // Payment link macro bindings
  { key: "PaymentProvider", label: "Payment Provider", description: "Payment provider name ('Omise' or 'Stripe')" },
  { key: "FullFeePercent", label: "Full Fee %", description: "Fee percentage for full payment (e.g., '1.85')" },
  { key: "PartialFeePercent", label: "Partial Fee %", description: "Fee percentage for deposit payment (e.g., '3.65')" },
  { key: "IsPaymentDueDatePassed", label: "Due Date Passed", description: "Boolean: true if 4 weeks before arrival has passed (use for conditionals)" },
];

// Context for binding values
const MacroBindingContext = createContext<MacroBindingValues | null>(null);

// Provider component
export interface MacroBindingProviderProps {
  value: MacroBindingValues;
  children?: React.ReactNode;
}

export function MacroBindingProvider({ value, children }: MacroBindingProviderProps) {
  return (
    <MacroBindingContext.Provider value={value}>
      {children}
    </MacroBindingContext.Provider>
  );
}

// Hook to access binding values
export function useMacroBindings(): MacroBindingValues {
  const ctx = useContext(MacroBindingContext);
  if (!ctx) {
    // Return defaults if no provider (useful for SSR or isolated rendering)
    return defaultPreviewBindings;
  }
  return ctx;
}

// Regex to match {{variable}} tokens (supports dotted keys like program.Name)
const TEMPLATE_TOKEN_REGEX = /\{\{\s*([a-zA-Z0-9_.]+)\s*\}\}/g;

// Check if a token is a valid binding key
function isValidBindingKey(token: string): token is MacroBindingKey {
  return MACRO_BINDING_KEYS.includes(token as MacroBindingKey);
}

// Resolve a single binding key to its value
export function resolveBinding(key: MacroBindingKey, values: MacroBindingValues): string {
  switch (key) {
    case "FirstName":
      return values.FirstName ?? "";
    case "ProgramDaysText":
      return values.ProgramDaysText ?? "";
    case "ReservationId":
      return values.ReservationId ?? "";
    case "RoomCount":
      return String(values.RoomCount ?? 0);
    case "TotalFitnessCount":
      return String(values.TotalFitnessCount ?? 0);
    case "WeightLossCount":
      return String(values.WeightLossCount ?? 0);
    case "QuoteLink":
      return values.QuoteLink ?? "";
    // Payment/deposit bindings
    case "DepositPercent":
      return String(values.DepositPercent ?? 50);
    case "DepositAmount":
      return values.DepositAmount ?? "";
    case "TotalPrice":
      return values.TotalPrice ?? "";
    case "RemainderAmount":
      return values.RemainderAmount ?? "";
    case "FullPaymentDueDate":
      return values.FullPaymentDueDate ?? "";
    case "PaymentLinkFull":
      return values.PaymentLinkFull ?? "";
    case "PaymentLinkPartial":
      return values.PaymentLinkPartial ?? "";
    case "RemainderDueText":
      return values.RemainderDueText ?? "";
    // Payment link macro bindings
    case "PaymentProvider":
      return values.PaymentProvider ?? "Stripe";
    // Fee recovery disabled — fallbacks mirror the 0% state set by the macro components.
    // See ReservationsApp/Sales/js/EditReservation/Macros/paymentFeeRates.ts (FEE_RECOVERY_ENABLED).
    case "FullFeePercent":
      return values.FullFeePercent ?? "0";
    case "PartialFeePercent":
      return values.PartialFeePercent ?? "0";
    case "IsPaymentDueDatePassed":
      return String(values.IsPaymentDueDatePassed ?? false);
    default:
      return "";
  }
}

/**
 * Extract the base string from various template input shapes (from Puck contentEditable).
 */
function extractTemplateString(template: unknown): string | null {
  if (template == null) {
    return null;
  }

  if (typeof template === "string") {
    return template;
  }

  if (typeof template === "object") {
    const anyTemplate = template as any;

    // Puck contentEditable textarea: { props: { value: "..." } }
    if (anyTemplate.props?.value && typeof anyTemplate.props.value === "string") {
      return anyTemplate.props.value;
    }
    // Generic shape: { value: "..." }
    if (anyTemplate.value && typeof anyTemplate.value === "string") {
      return anyTemplate.value;
    }
    // Shape with text property
    if (anyTemplate.text && typeof anyTemplate.text === "string") {
      return anyTemplate.text;
    }
    // Shape with children property
    if (anyTemplate.children && typeof anyTemplate.children === "string") {
      return anyTemplate.children;
    }
  }

  return null;
}

// Render a template string by substituting {{variable}} tokens
export function renderTemplate(template: unknown, values: MacroBindingValues): string {
  const base = extractTemplateString(template);
  if (!base) {
    return "";
  }

  // Replace all {{variable}} tokens
  return base.replace(TEMPLATE_TOKEN_REGEX, (match, rawToken: string) => {
    const token = rawToken.trim();
    if (isValidBindingKey(token)) {
      return resolveBinding(token, values);
    }
    // Keep unrecognized tokens as-is (for collection bindings resolved via hook, or Scriban in SSR)
    return match;
  });
}

/**
 * Extended render function that also resolves collection bindings.
 * Must be used within appropriate iteration providers (ProgramIterationProvider, etc.)
 */
export function renderTemplateWithCollections(
  template: unknown,
  values: MacroBindingValues,
  programCtx: ReturnType<typeof useProgramIteration>,
  roomCtx: ReturnType<typeof useRoomIteration>,
  posCtx: ReturnType<typeof usePosIteration>
): string {
  const base = extractTemplateString(template);
  if (!base) {
    return "";
  }

  // Replace all {{variable}} tokens
  return base.replace(TEMPLATE_TOKEN_REGEX, (match, rawToken: string) => {
    const token = rawToken.trim();

    // First check collection bindings (program.*, room.*, pos.*)
    if (isCollectionBindingKey(token)) {
      return resolveCollectionBinding(token as CollectionBindingKey, programCtx, roomCtx, posCtx);
    }

    // Then check standard bindings
    if (isValidBindingKey(token)) {
      return resolveBinding(token, values);
    }

    // Keep unrecognized tokens as-is (for Scriban conditionals in SSR)
    return match;
  });
}

// Hook that provides a memoized renderTemplate function bound to current context
export function useRenderTemplate() {
  const bindings = useMacroBindings();

  return useMemo(() => {
    return (template: unknown) => renderTemplate(template, bindings);
  }, [bindings]);
}

/**
 * Hook that provides a memoized renderTemplate function that includes collection binding support.
 * Use this within repeater blocks to render templates with {{program.*}}, {{room.*}}, {{pos.*}} bindings.
 */
export function useRenderTemplateWithCollections() {
  const bindings = useMacroBindings();
  const programCtx = useProgramIteration();
  const roomCtx = useRoomIteration();
  const posCtx = usePosIteration();

  return useMemo(() => {
    return (template: unknown) =>
      renderTemplateWithCollections(template, bindings, programCtx, roomCtx, posCtx);
  }, [bindings, programCtx, roomCtx, posCtx]);
}

// Generate quote link from reservation ID
export function generateQuoteLink(reservationId: string): string {
  return `https://reservations.phuketfit.com/quotes/${reservationId}`;
}

// Helper to create binding values from partial input
export function createBindingValues(partial: Partial<MacroBindingValues>): MacroBindingValues {
  const base = { ...defaultPreviewBindings, ...partial };

  // Auto-generate QuoteLink if ReservationId is provided but QuoteLink is not
  if (partial.ReservationId && !partial.QuoteLink) {
    base.QuoteLink = generateQuoteLink(partial.ReservationId);
  }

  return base;
}
